import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { AuthFacade } from 'src/app/store/auth/facade/auth.facade';
import { ITradingState } from './../../state/order.state';
import { SiteIds } from './../../../models/sites';
import { AppConfig } from './../../../app.config';
import { FeatureToggleService } from 'src/app/services/feature-toggle/feature-toggle.service';
import { Features } from 'src/app/models/features';
import { ClientEntity } from 'src/app/models/clients';
import { GeneralSelectionFacade } from 'src/app/store/general-selection/facade/general-selection.facade';
import {
    Product,
    CancelOrderPayload,
    InvestmentPlan,
    InstructionStatus,
    ApproveOrderPayload,
    TermDepositOrderPayload,
    IOrderBase, EquityOrderPayload, OrderAction,
    RegularInvestmentStatus
} from "../../../models/orders";
import { PortfoliosFacade } from 'src/app/store/portfolios/facade/portfolios.facade';
import { computed, Injectable, OnDestroy, signal } from "@angular/core";
import { IAppState } from "../../state/app.state";
import { Store, select } from "@ngrx/store";
import {Observable, Subscription, BehaviorSubject, combineLatest, of, Subject} from "rxjs";
import * as fromTrading from "../selectors/trading.selectors";
import * as TradingActions from "../actions/trading.actions";
import * as PortfolioActions from "../../portfolios/actions/portfolios.actions";
import { IPortfolio } from 'src/app/models/portfolios';
import { CreateApplicationPayload, CreateRedemptionOrderPayload, Order } from 'src/app/models/orders';
import { tap, map, take, filter, catchError, finalize, switchMap } from "rxjs/operators";
import { areArraysUnequal } from 'src/app/utils/comparative.methods';
import * as moment from 'moment';
import { NotificationCategory, UserNotification } from 'src/app/models/userNotification';
import { TokenService } from 'src/app/services/token/token.service';
import { UserRoleType } from 'src/app/models/userRoleTypes';
import { TradingService } from "src/app/services/http/trading.service";
import { SnackBarService } from "src/app/services/snackbar/snackBar.service";
import { CashflowFacade } from "src/app/store/cashflow/facade/cashflow.facade";
import { ReinvestmentFacade } from "./reinvestment.facades";
import { DecimalPipe } from "@angular/common";

@UntilDestroy()
@Injectable()
export class TradingFacades implements OnDestroy {
    portfolioSubscription: Subscription;
    selectedPortfolio: IPortfolio;
    getKiwisaverEntitySubscription: Subscription;
    getAvailablePortfoliosSubscription: Subscription;
    portfolioId: BehaviorSubject<number> = new BehaviorSubject(null);
    portfolioId$: Observable<number> = this.portfolioId.asObservable();
    combinedProductsPortfolioId$: Observable<[Product[], number]>;
    selectedEntities: ClientEntity[];

    tradeStateLoading : boolean;
    selectedEntitiesSubscription : Subscription;

    isAdministrator = this.tokenService.isAdministrator();
    public loading = signal(false);
    isAdvisor = this.authFacade.doesUserHaveRole(UserRoleType.Advisor);

    public orders$: Observable<Order[]> = this.store.pipe(select(fromTrading.selectCurrentOrderState));
    private isUserLoggedIn$: Observable<boolean> = this.authFacade.userLoggedIn$();

    public isLoading = signal<boolean>(false);
    private isInvestmentPlanLoading = signal<boolean>(false);
    public isProductsLoading = signal<boolean>(false);
    public isInvestmentPlanOrProductsLoading = computed(() => this.isInvestmentPlanLoading() || this.isProductsLoading());

    private userLoggedInSubscription: Subscription = this.isUserLoggedIn$.pipe(untilDestroyed(this)).subscribe((isLoggedIn: boolean) => {
        if (!isLoggedIn) {
            this.resetValues();
        }
    });

    /**
     * A behaviour that can be used to prompt a reload of regular investment plan.
     */
    public loadInvestmentPlan$ = new Subject<void>();


    private entityPortfolioCombined$: Observable<any> = combineLatest([
        this.portfoliosFacade.getAvailablePortfolios(),
        this.portfoliosFacade.getEntitiesPortfolios(),
        this.generalSelectionfacade.getSelectedInvestorEntities()
    ]);

    constructor(
        private store: Store<IAppState>,
        private portfoliosFacade: PortfoliosFacade,
        private generalSelectionfacade: GeneralSelectionFacade,
        private featureToggleService: FeatureToggleService,
        private appConfig: AppConfig,
        private authFacade: AuthFacade,
        private tokenService: TokenService,
        private tradingService: TradingService,
        private snackBarService: SnackBarService,
        private cashFlowFacade: CashflowFacade,
        private reinvestmentFacade: ReinvestmentFacade,
        private decimalPipe: DecimalPipe
    ) {
        this.initialiseData();
    }

    private initialiseData() {
        this.subscribeToSelectedPortfolioId();
        this.subscribeToSelectedEntities();

    }

    private resetValues() {
        this.selectedPortfolio = undefined;
        this.selectedEntities = undefined;
        this.portfolioId.next(null);
    }

    private subscribeToSelectedPortfolioId() {
        this.entityPortfolioCombined$
        .pipe(
            untilDestroyed(this)
        )
        .subscribe((v: [IPortfolio[], IPortfolio[], ClientEntity[]]) => {
            const [availablePortfolios, entitiesPortfolios, clientEntities] = v;

            let combinedPortfolios: IPortfolio[] = this.getDistinctPortfolios([availablePortfolios, entitiesPortfolios]);

            if (combinedPortfolios && combinedPortfolios.length > 0 && clientEntities && clientEntities.length > 0) {
                let selectedPortfolio: IPortfolio;

                clientEntities.forEach((ce) => {
                    const sp = combinedPortfolios.find((p) => p.entityId === ce.EntityId);
                    if (sp) {
                        selectedPortfolio = sp;
                        return;
                    }
                });

                if (!this.selectedPortfolio || (selectedPortfolio && selectedPortfolio.id !== this.selectedPortfolio.id)) {
                    this.selectedPortfolio = selectedPortfolio;
                    this.portfolioId.next(this.selectedPortfolio.id);
                }
            } else {
                this.portfolioId.next(null);
                this.selectedPortfolio = null;
            }
        });
    }

    /**
     * Flattens an array of portfolio arrays and removes duplicate values from the collection.
     * @param portfolios an array of portfolio arrays
     * @returns an array of unique portfolios
     */
    private getDistinctPortfolios(portfolios: IPortfolio[][]): IPortfolio[] {
        let flat = [
            ...new Set(
                portfolios.flatMap(p => p || [])
            )
        ]
        
        return flat;;
    }

    private subscribeToSelectedEntities() {
        this.selectedEntitiesSubscription = this.generalSelectionfacade.getSelectedInvestorEntities()
            .subscribe((selectedEntities : ClientEntity[]) => {
                if (selectedEntities.length > 0) {
                    if (this.selectedEntities === undefined
                        || this.selectedEntities === null
                        || this.selectedEntities.length === 0
                        || areArraysUnequal(selectedEntities, this.selectedEntities, "EntityId"))
                    {
                        this.selectedEntities = selectedEntities;
                        if (this.checkLoadOrders()) {
                            this.loadOrders();
                        }

                        this.loadInvestmentPlanForEntity(selectedEntities[0].EntityId);
                    }
                }
            });
    }

    private checkLoadOrders() : boolean {
        if(this.tradeStateLoading)
            return false;

        // the check 'is Buy Sell enabled' should be done differently so that the service doesn't have to be called
        // and feature permissions act as a stream to subscribe to. In that way we always have the latest permissions state
        return this.canLoadOrders();
    }

    private canLoadOrders() {
        return this.featureToggleService.featureOn(Features.BUYSELL) || this.featureToggleService.featureIndividualInvestorOn(Features.BUYSELL) || this.featureToggleService.featureOn(Features.TERMDEPOSITS);
    }
    
    public useTermDepositWithBuySellOff() :boolean{
        return !this.featureToggleService.featureOn(Features.BUYSELL) 
            && this.featureToggleService.featureOn(Features.TERMDEPOSITS);
    }

    public getTradingLoadingState(): Observable<boolean> {
        return this.store.pipe(
            select(fromTrading.getTradingState),
            map((tradingState: ITradingState) => {
                this.tradeStateLoading = tradingState.loading;
                return tradingState.loading;
            })
        );
    }

    public getProducts(): Observable<Product[]> {

        const productsObservable = this.store.pipe(
            select(fromTrading.selectProductsState)
        );

        this.combinedProductsPortfolioId$ = combineLatest([
            productsObservable, 
            this.portfolioId$
        ])
        .pipe(
            tap(res => {
                const [products, portfolioId] = res;
                if (!products && portfolioId) {
                    this.loadProducts(portfolioId);
                }
            }
        ));

        return this.combinedProductsPortfolioId$.pipe(map((res) => res[0]));
    }

    /**
     * An observable that returns the available products for the selected portfolio.
     * The products will automatically reload when the portfolio id changes.
     */
    public products$ = this.portfolioId$.pipe(
        untilDestroyed(this),
        tap(() => this.isProductsLoading.set(true)),
        filter((portfolioId) => !!portfolioId),
        switchMap((portfolioId) => {
            return this.tradingService.getProducts(portfolioId)
            .pipe(
                finalize(() => {
                    this.isProductsLoading.set(false)
                }),
                catchError(() => of(null))
            )
        }),
    )

    /**
     * An observable that returns the regular investment plan for the selected portfolio.
     * The investment plan will reload either when the portfolio id changes
     * or the load invesmtent plan subject emits.
     */
    public investmentPlan$ = combineLatest([
        this.portfolioId$,
        this.loadInvestmentPlan$
    ]).pipe(
        untilDestroyed(this),
        tap(() => this.isInvestmentPlanLoading.set(true)),
        filter((portfolioId) => !!portfolioId),
        switchMap(([portfolioId, _]) => {
            return this.tradingService.getRegularInvestmentPlan(portfolioId)
            .pipe(
                finalize(() => {
                    this.isInvestmentPlanLoading.set(false)
                }),
                catchError(() => of(null))
            )
        })
    );

    private loadProducts(portfolioId: number) {
        this.store.dispatch(new TradingActions.GetAvailableProducts(portfolioId));
    }

    public getOrders(): Observable<Order[]> {
        return combineLatest([
            this.orders$,
            this.isUserLoggedIn$
        ]).pipe(
            filter((values: [Order[], boolean]) => {
                const [orders, isLoggedIn] = values;
                return isLoggedIn;
            }),
            map((values: [Order[], boolean]) => {
                const [orders, isLoggedIn] = values;
                return orders;
            })
        );
    }

    public getOrderNotifications(): Observable<UserNotification[]> {
        return this.getOrders().pipe(map((orders: Order[]) => {
            if (!orders) {
                return []; // TODO: this should be returned by the store. no mutating state in facade.
            }

            var ordersWithApprovalRequried = orders.filter(x => x.status === InstructionStatus.ApprovalRequired)

            return ordersWithApprovalRequried.map(order => {

                const notification: UserNotification = {
                    notificationDescription : this.getOrderDescription(order),
                    notificationDate : moment(order.createdAt).format("dddd MMMM Do YYYY"),
                    notificationCategory : NotificationCategory.ORDER,
                    notificationLink: ["client/buy-sell"]
                };

                return notification;
            });
        }));
    }

    private getOrderDescription(order: Order) {
        return this.getOrderStatusText(order) + " for " + order.description + " of " + order.data.securityName;
    }

    private getOrderStatusText(order: Order) {
        switch (order.status) {
            case InstructionStatus.ReviewRequired:
                return "No Action Required";
            default:
                return order.statusName;
        }
    }

    public loadOrders() {
        this.store.dispatch(new TradingActions.GetCurrentOrders(this.selectedEntities));
    }

    public createApplication(orderPayload: CreateApplicationPayload) {
        if (this.selectedPortfolio) {
            this.isLoading.set(true);
            return this.tradingService.createApplication(
                this.selectedPortfolio.id,
                orderPayload
            ).pipe(
                map((res: any[]) => {
                    if (res) {

                        this.snackBarService.showSnackBar(this.generateSuccessfulBuyOrderCreationMessage(orderPayload.Amount.value, orderPayload.Amount.currency, orderPayload));
                        this.loadOrders();
                        this.cashFlowFacade.requestAvailableCashflowData(
                            true
                        );
                    }
                }),
                catchError(() => {
                    this.snackBarService.showWarningSnackBar(
                        `Your buy order for <strong>${this.decimalPipe.transform(orderPayload.Amount.value, '1.2-2')} ${orderPayload.Amount.currency} of ${orderPayload.Name}</strong> was unable to be placed. Please try again.`
                    );
                    return of(new TradingActions.UpdateStatus());
                }),
                finalize(() => {
                    this.isLoading.set(false);
                })
            );
        }

        this.snackBarService.showWarningSnackBar("No portfolio selected");
        return of();
    }

    public createRedemption(orderPayload: CreateRedemptionOrderPayload): Observable<any> {
        if (this.selectedPortfolio) {
            this.isLoading.set(true);
            return this.tradingService.createRedemption(
                this.selectedPortfolio.id,
                orderPayload
            ).pipe(
                map((res: any[]) => {
                    if (res) {

                        this.snackBarService.showSnackBar(this.generateSuccessfulSellOrderCreationMessage(orderPayload.Quantity, orderPayload));
                        this.loadOrders();
                        return new TradingActions.UpdateStatus();

                    }
                }),
                catchError(() => {
                    this.snackBarService.showWarningSnackBar(
                        `Your sell order for <strong>${this.decimalPipe.transform(orderPayload.Quantity, '1.4-4')} Units of ${orderPayload.Name}</strong> was unable to be placed. Please try again.`
                    );
                    return of(new TradingActions.UpdateStatus());
                }),
                finalize(() => {
                    this.isLoading.set(false);
                })
            );
        }

        this.snackBarService.showWarningSnackBar("No portfolio selected");
        return of();
    }

    public createEquityOrder(orderPayload: EquityOrderPayload, currency: string): Observable<any> {
        if (this.selectedPortfolio) {
            this.isLoading.set(true);
            return this.tradingService.createEquityOrder(
                this.selectedPortfolio.id,
                orderPayload,
            ).pipe(
                map((res: any[]) => {
                    if (res) {
                        if(orderPayload.Action === OrderAction.Buy) {
                            this.snackBarService.showSnackBar(this.generateSuccessfulBuyOrderCreationMessage(orderPayload.Amount, currency, orderPayload));
                        }
                        else
                        {
                            this.snackBarService.showSnackBar(this.generateSuccessfulSellOrderCreationMessage(orderPayload.Quantity, orderPayload));
                        }
                        this.loadOrders();
                        return new TradingActions.UpdateStatus();
                    }
                }),
                catchError(() => {
                    if(orderPayload.Action === OrderAction.Buy){
                        this.snackBarService.showWarningSnackBar(
                            `Your buy order for <strong>${orderPayload.Amount} ${currency} of ${orderPayload.Name}</strong> was unable to be placed. Please try again.`
                        );
                    }else{
                        this.snackBarService.showWarningSnackBar(
                            `Your sell order for <strong>${this.decimalPipe.transform(orderPayload.Quantity, '1.4-4')} Units of ${orderPayload.Name}</strong> was unable to be placed. Please try again.`
                        );
                    }
                    return of(new TradingActions.UpdateStatus());
                }),
                finalize(() => {
                    this.isLoading.set(false);
                })
            );
        }

        this.snackBarService.showWarningSnackBar("No portfolio selected");
        return of();
    }

    private generateSuccessfulBuyOrderCreationMessage(amount: number, currency: string, buyOrder: IOrderBase) {
        const orderTiming =
            this.appConfig.getEnvironment().orderTiming
                ? this.appConfig.getEnvironment()
                    .orderTiming
                : '12pm daily';
        
        return `Success! You've bought <strong>${this.decimalPipe.transform(amount, '1.2-2')} ${currency} of ${buyOrder.Name}</strong>. Your order is pending until they are processed at ${orderTiming} and it will show in your portfolio within 2 business days.`;
    }

    private generateSuccessfulSellOrderCreationMessage(quantity: number, sellOrder: IOrderBase) {
        const walletName = this.appConfig.getEnvironment()
            .walletName
            ? this.appConfig.getEnvironment().walletName
            : 'Cash account';
        const orderTiming = this.appConfig.getEnvironment()
            .orderTiming
            ? this.appConfig.getEnvironment().orderTiming
            : '12pm daily';

        return `Success! You've sold <strong>${this.decimalPipe.transform(quantity, '1.4-4')} Units of ${sellOrder.Name}</strong>. Your order is pending until they are processed at ${orderTiming} and it will show in your ${walletName} within 2 business days.`
    }

    public cancelPendingOrder(payload: CancelOrderPayload) {
        this.store.dispatch(new TradingActions.CancelPendingOrder(payload));
    }

    public approveOrder(payload: ApproveOrderPayload) {
        this.store.dispatch(new TradingActions.ApproveOrder(payload));
    }

  //---------------------------------------------------------------------------------------------

    /**
    * Attempts to retrieve the regular investment plan from state. If there is no
    * plan in state attempts to retrieve the investment plan.
    */
    public getRegularInvestmentPlan(): Observable<Partial<InvestmentPlan>> {
        return this.store
        .pipe(
            select(fromTrading.selectRegularInvestmentPlan),
            tap((investmentPlan: InvestmentPlan) => {
                if (!investmentPlan) {
                    this.loadInvestmentPlanForEntity();
                }
            })
        );
    }

    // Fetch the investment plan for the selected entity, or first available portfolio if not supplied.
    private loadInvestmentPlanForEntity(entityId?: number) : void {

        let investorId: number = this.resolveEntityId(entityId);

        //This is so rubbish but basically we have to retrieve the portfolios in different ways depending on the user level (i.e. admin, advisor, investor).
        //We cant fully trust either portfolio retrieval method to work for all three 'user levels'.
        const localPortfolioSubscription = this.isAdvisor || this.isAdministrator ?
            this.portfoliosFacade.getEntitiesPortfolios() :
            this.portfoliosFacade.getAvailablePortfolios();

        if (this.getAvailablePortfoliosSubscription){
            this.getAvailablePortfoliosSubscription.unsubscribe();
        }

        this.getAvailablePortfoliosSubscription = localPortfolioSubscription
        .pipe(
            filter((portfolios: IPortfolio[]) => portfolios != null)
        )
        .subscribe((portfolios: IPortfolio[]) => {
            const portfolio = investorId != null ? portfolios.find(p => p.entityId === investorId) : portfolios[0];
            if (portfolio) {
                this.loadInvestmentPlanForPortolio(portfolio.id);
            } else if (this.appConfig.getEnvironment().siteId === SiteIds.INVESTNOW) {
                this.store.dispatch(new TradingActions.GetRegularInvestmentPlanNotFound());
            }
        });
    }

    public loadInvestmentPlanForPortolio(portfolioId: number): void {
        this.store.dispatch(new TradingActions.GetRegularInvestmentPlan(portfolioId));
    }

    public dispatchSaveRegularInvestmentPlanAction(payload: InvestmentPlan) : void {
        this.store.dispatch(new TradingActions.SaveRegularInvestmentPlan(payload));
    }

    /**
     * Performs a save of a regular investment plan by calling directly to the service.
     * This avoids additional side actions from being performed when saving through dispatching the action.
     */
    public saveRegularInvestmentPlan(regularInvestmentPlan: InvestmentPlan) : Observable<any> {
        return this.tradingService.saveRegularInvestmentPlan(regularInvestmentPlan);
    }

    /**
     * Dispatches the same actions as the trading effect on successful update of a regular invesmtent plan.
     */
    public regularInvestmentPlanUpdateSuccessfulActions(investmentPlan: InvestmentPlan) : void {
        this.store.dispatch(new TradingActions.GetRegularInvestmentPlanSuccess(investmentPlan));
        this.store.dispatch(new PortfolioActions.GetInvestorPortfolios());
        this.store.dispatch(new TradingActions.UpdateStatus());
    }

    /**
     * Dispatches the same actions as the trading effect on unsuccessful update of a regular invesmtent plan.
     * @param investmentPlan the regular investment plan passed to the effect to be saved. I am not sure if the status was persisted on a failed update.
     */
    public regularInvestmentPlanUpdateUnsuccessfulActions(investmentPlan: InvestmentPlan) : void {
        this.store.dispatch(new TradingActions.GetRegularInvestmentPlanSuccess(
        {
            ...investmentPlan,
            status: RegularInvestmentStatus.Incomplete,
        }
        ));
        this.store.dispatch(new TradingActions.UpdateStatus());
    }

  //---------------------------------------------------------------------------------------------

    resolveEntityId(explicitEntityId?: number) : number | null {
        //If we are explicitly given an entity id use it
        if (!!explicitEntityId){
            return explicitEntityId
        }
        //Else we use the selected entity - take the first one for now.
        //Will have to rework for multiple entities.
        if (this.entitySelected()){
            return this.selectedEntities[0].EntityId;
        }

        return null
    }

    entitySelected() : boolean {
        return !!this.selectedEntities && this.selectedEntities.length > 0;
    }

    ngOnDestroy(): void {
        this.portfolioSubscription.unsubscribe();
        this.selectedEntitiesSubscription.unsubscribe();
        this.getAvailablePortfoliosSubscription.unsubscribe();
    }

    public createTermDepositOrder(createTermDepositPayload: TermDepositOrderPayload): Observable<void> {

        if(this.selectedPortfolio){
            this.loading.set(true);
            return this.tradingService
                .createTermDepositOrder(this.selectedPortfolio.id, createTermDepositPayload)
                .pipe(
                    map((res: any[]) => {
                        if (res) {
                            this.snackBarService.showSnackBar(
                                this.generateSuccessfulTermDepositOrderMessage(createTermDepositPayload),
                            );
                            this.loadOrders();
                            this.cashFlowFacade.requestAvailableCashflowData(true)
                            this.reinvestmentFacade.loadReinvestmentInstructions();
                        }
                    }),
                    catchError(() => {
                        this.snackBarService.showWarningSnackBar(
                            `Your term deposit instruction - <strong>Invest ${this.decimalPipe.transform(createTermDepositPayload.amount, '1.2-2')} ${createTermDepositPayload.currency}, ${createTermDepositPayload.bankName}, ${createTermDepositPayload.durationName} Term Deposit, ${createTermDepositPayload.expectedRate}%</strong> -  was unable to be placed. Please try again.`,
                        );
                        return of();
                    }),
                    finalize(() => this.loading.set(false))
                );
        }

        this.snackBarService.showWarningSnackBar("No portfolio selected");
        return of();
    }

    private generateSuccessfulTermDepositOrderMessage(createTermDepositPayload: TermDepositOrderPayload){
         return `Success! Your term deposit instruction - <strong>Invest ${this.decimalPipe.transform(createTermDepositPayload.amount, '1.2-2')} ${createTermDepositPayload.currency}, ${createTermDepositPayload.bankName}, ${createTermDepositPayload.durationName} Term Deposit, ${createTermDepositPayload.expectedRate}%</strong> - has been submitted successfully and will now appear in your open orders`;
    }
}
